<template>
  <div data-testid="invoices-table">
    <data-table
      :config="datatableConfig"
      :data="rows"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      :pdfButton="pdfButtonConfig"
      :printButton="printButtonConfig"
      :clipboardButton="clipboardButtonConfig"
      @edit-click="handleEditClick"
      @row-click="handleRowClick"
      @refresh="emit('refresh')" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../../grid/DataTable.vue';
import { eventBus } from '../../../eventBus';
import type { InvoiceListRow } from '../../../composables/usePaymentInvoices';

withDefaults(defineProps<{
  rows: InvoiceListRow[];
  loading?: boolean;
}>(), {
  loading: false,
});

const emit = defineEmits<{
  'row-click': [id: number];
  'refresh': [];
}>();

const { t } = useI18n();

const statusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    draft:      t('Rascunho'),
    submitted:  t('Enviada'),
    authorized: t('Autorizada'),
    rejected:   t('Rejeitada'),
    cancelled:  t('Cancelada'),
    error:      t('Erro'),
  };
  return labels[status] || status;
};

const statusSeverity = (status: string): string => {
  const map: Record<string, string> = {
    draft:      'secondary',
    submitted:  'info',
    authorized: 'success',
    rejected:   'danger',
    cancelled:  'secondary',
    error:      'danger',
  };
  return map[status] || 'secondary';
};

// Open the customer modal for the clicked invoice row. Distinct from the ID
// column's invoice modal: the Cliente cell is a custom_clickable_id whose
// clickCallback routes to the cliente domain via eventBus, keyed on the
// backend-resolved cliente_id carried on the row.
const openCustomer = (_value: unknown, row: Record<string, unknown>): void => {
  const invoiceRow = row as InvoiceListRow;
  const clienteId = invoiceRow.cliente_id;
  if (clienteId != null) {
    eventBus.emit('modal.cliente.open', { id: Number(clienteId) });
  } else {
    // No backend-resolved cliente match for this document — open the customer
    // modal in create/prefill mode seeded with the invoice's customer name.
    eventBus.emit('modal.cliente.open', { prefillName: invoiceRow.customer_name ?? '' });
  }
};

const tableColumns = computed(() => [
  {
    data: 'id',
    title: t('ID'),
    width: 120,
    sortable: true,
    renderType: 'clickable_id',
    mergedColumns: [
      {
        data: 'franchise_name',
        title: t('Franquia'),
      },
    ],
  },
  {
    data: 'invoice_number',
    title: t('Número'),
    width: 140,
    sortable: true,
  },
  {
    data: 'status',
    title: t('Status'),
    width: 130,
    sortable: true,
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>) => {
      const status = String(row.status ?? '');
      return {
        label: statusLabel(status),
        severity: statusSeverity(status),
        tooltip: statusLabel(status),
      };
    },
  },
  {
    data: 'customer_name',
    title: t('Cliente'),
    width: 240,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: openCustomer,
    mergedColumns: [
      {
        data: 'customer_doc',
        title: t('Document'),
      },
      {
        data: 'customer_phone',
        title: t('Telefone'),
      },
      {
        data: 'customer_email',
        title: t('E-mail'),
      },
    ],
  },
  {
    data: 'total_amount',
    title: t('Total'),
    width: 130,
    sortable: true,
    renderType: 'currency',
  },
  {
    data: 'emission_date',
    title: t('Emissão'),
    width: 130,
    sortable: true,
    renderType: 'date',
  },
  {
    data: 'provider_code',
    title: t('Provedor'),
    width: 120,
    sortable: true,
  },
]);

const datatableConfig = computed(() => ({
  tableId: 'invoicesDataTable',
  title: t('Faturas'),
  filename: `faturas_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pageLength: 25,
  height: '500px',
  footer: false,
  rowSelection: 'single',
  emptyMessage: t('Nenhuma fatura emitida no período selecionado.'),
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.id;
    return id == null ? '' : `invoices-row-${id}`;
  },
}));

const filterConfig = computed(() => ({
  searchName: t('Procurar'),
  choosePlaceholder: t('Escolha'),
  refreshName: t('Atualizar'),
  printName: t('Imprimir'),
  columns: {
    id: 'text',
    invoice_number: 'text',
    status: 'select',
    customer_name: 'text',
    total_amount: 'number',
    emission_date: 'date',
    provider_code: 'text',
  },
}));

const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));
const csvButtonConfig = computed(() => ({ show: true, text: t('CSV') }));
const pdfButtonConfig = computed(() => ({ show: true, text: t('PDF') }));
const printButtonConfig = computed(() => ({ show: true, text: t('Imprimir') }));
const clipboardButtonConfig = computed(() => ({ show: true, text: t('Copiar') }));

const handleEditClick = (id: number): void => {
  emit('row-click', Number(id));
};

const handleRowClick = (rowData?: { id?: number } | null): void => {
  if (rowData?.id != null) emit('row-click', Number(rowData.id));
};
</script>
